import { transition, trigger, useAnimation } from '@angular/animations'
import { Component, computed, inject } from '@angular/core'
import {
  CartState,
  Experiment,
  HardwareId,
  ProductKey,
  RegistrationFlow,
  TargetAudience,
  WebSignupQueryParam,
} from '@naturalcycles/shared'
import { CartService } from '@src/app/core/services/cart.service.js'
import { NavService } from '@src/app/core/services/nav.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { fadeAnimation } from '@src/app/core/util/animations.util.js'
import {
  ActivatePage,
  ClearbluePage,
  ManageAccountPage,
  Page,
  ParentPage,
} from '@src/app/shared/typings/enum/pages.js'
interface USP {
  emoji: string
  text: string
}

@Component({
  selector: 'app-page-code',
  templateUrl: './secret.page.component.html',
  styleUrls: ['./secret.page.component.scss'],
  animations: [trigger('fadeAnimation', [transition(':enter', [useAnimation(fadeAnimation)])])],
  standalone: false,
})
export class SecretPage {
  private cartService = inject(CartService)
  private navService = inject(NavService)
  private store = inject(SignalStore)

  protected code = this.navService.getQueryParam(WebSignupQueryParam.code)
  protected referral =
    this.navService.getQueryParam(WebSignupQueryParam.referral) ||
    this.navService.getQueryParam(WebSignupQueryParam.refId)

  protected ignoreConfirmation =
    this.navService.getQueryParam(WebSignupQueryParam.ignoreConfirmation) === 'true'

  private ab354Bucket = computed(() => {
    return this.store.$experiment().assignments[Experiment.WS_OURA_NO_IGNORED_CONFIRMATION]?.bucket
  })

  protected forceShowConfirmation = computed(() => {
    const ab354 = this.ab354Bucket()
    return ab354 === 'test' && this.code?.toLowerCase() === 'ouranctrial'
  })

  protected USPs = computed<USP[]>(() => {
    const cart = this.store.$cart()
    const userLocale = this.store.$userLocale()
    const isUS = userLocale.country === 'US'
    const hasBluetoothTherm = cart.items.some(item => item.key === ProductKey.BT_THERMOMETER)
    return this.getUSPs(isUS, hasBluetoothTherm)
  })

  private getUSPs(isUS: boolean, hasBluetoothTherm?: boolean): USP[] {
    return [
      { emoji: '🏅', text: 'usp-approved' },
      isUS
        ? { emoji: '💳', text: 'usp-fsa-covered' }
        : hasBluetoothTherm
          ? { emoji: '🌡📲', text: 'usp-bluetooth-thermometer' }
          : { emoji: '🎯', text: 'usp-effective' },

      { emoji: '✅', text: 'usp-hormone-free' },
    ]
  }

  async continue(): Promise<boolean> {
    const cart = this.store.$cart()
    const discountCode = this.store.$discountCode()
    const requireOuraQuestion = cart.hwId === HardwareId.OURA && !this.isExistingOuraUser(cart)
    const isActiveOrChurnedCode = discountCode?.targetAudience === TargetAudience.activeOrChurned
    if (isActiveOrChurnedCode) {
      return await this.navService.goToPage([ManageAccountPage.account, Page.login])
    }

    if (requireOuraQuestion) {
      return await this.navService.goToPage([Page.oura])
    }

    if (this.isExistingOuraUser(cart)) {
      await this.cartService.selectDevice(HardwareId.OURA, ProductKey.OURA_RING_DISCOUNT, true)
      return await this.navService.goToPage([Page.measuringDevice], {
        queryParams: {
          [WebSignupQueryParam.flow]: RegistrationFlow.OURA.toString(),
        },
      })
    }
    const hasClearblueCodeApplied = this.cartService.hasClearblueCodeApplied(discountCode)
    if (hasClearblueCodeApplied) {
      return await this.navService.goToPage([ParentPage.clearblue, ClearbluePage.success])
    }

    const hasActivationCodeApplied = this.cartService.hasActivationCodeApplied(cart)
    if (hasActivationCodeApplied) {
      return await this.navService.goToPage(['activate', ActivatePage.credentials])
    }

    return await this.navService.goToPage([Page.measuringDevice])
  }

  private isExistingOuraUser(cart: CartState): boolean {
    return cart.discount?.code?.toLowerCase() === 'ouranctrial'
  }
}
